/**
 * Line component module.
 * @module shared/components/Line
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import moment from 'moment';
import {formatter} from '../../utils/utils';
import 'react-modern-calendar-datepicker/lib/DatePicker.css';
import {withTheme} from 'styled-components/macro';

// Line graph max size of tile
const Line = ({height = '100%', width = '100%', data, theme}) => {
  const options = React.useMemo(() => ({
    title: {
      text: '',
    },
    useHighStocks: true,
    series: [
      {
        dashStyle: 'solid',
        data,
      },
    ],
    rangeSelector: {
      enabled: false,
    },
    scrollbar: {
      enabled: false,
    },
    legend: {enabled: false},
    credits: {enabled: false},
    colors: [theme.neumorphic ? theme.primaryColor : '#00D0BE'],
    // TODO Replace with this eventually theme.primaryColor
    chart: {
      type: 'areaspline',
      panning: false,
      backgroundColor: null,
      plotBorderWidth: 0,
      borderRadius: 0,
      plotShadow: false,
      zoomType: '',
      pinchType: '',
    },
    xAxis: {
      lineColor: '#888',
      startOnTick: false,
      endOnTick: false,
      opposite: false,
      gridLineWidth: 0,
      tickLength: 0,
      labels: {
        overflow: true,
        align: 'center',
        style: {
          color: '#888',
        },
        formatter: function xFormatter() {
          return moment(this.value).format('M/D/YY');
        },
      },
    },
    yAxis: {
      title: {
        text: false,
      },
      opposite: false,
      offset: 0,
      gridLineColor: '#888',
      min: Math.min(...data.map(d => d[1])) * 0.8,
      max: Math.max(...data.map(d => d[1])) * 1.1,
      labels: {
        align: 'center',
        style: {
          color: '#888',
        },
        formatter: function yFormatter() {
          if ((options.yAxis.max >= 1100000)
            || (options.yAxis.max < 1100000 && options.yAxis.max > 1000000 && options.yAxis.min >= 500000)) {
            return this.value / 1000000 + 'M';
          }
          if ((options.yAxis.max < 1100000 && options.yAxis.max >= 1000)
            || (options.yAxis.max < 1500000 && options.yAxis.max > 1000000 && options.yAxis.min < 500000)) {
            return this.value / 1000 + 'k';
          }
          return this.value;
        },
      },
    },
    tooltip: {
      backgroundColor: '#FFFFFF',
      borderColor: '#FFFFFF',
      useHTML: true,
      formatter: function lineFormatter() {
        const p = this.point || this.points[0];
        const date = formatter('date')(moment(p.x).format('YYYY-MM-DD'));
        const balance = formatter('currency', {noDecimals: 1})(p.y);
        return `<div>${date}:</div><div style='font-size: 13px;margin-top: 3px;' class='text-center'>${balance}</div>`;
      },
      followTouchMove: false,
      crosshair: [false, false],
    },
    plotOptions: {
      areaspline: {
        lineWidth: 4,
        states: {
          hover: {
            lineWidth: 4,
          },
        },
      },
      series: {
        marker: {
          enabled: false,
          radius: 3,
          fillColor: '#000000',
          lineColor: '#FFFFFF',
          lineWidth: 1,
        },
        fillColor: {
          linearGradient: [0, 0, 0, '75%'],
          stops: [
            [0, theme.neumorphic ? theme.primaryColor : '#00D0BE'], // TODO replace with  theme.primaryColor eventually
            [1, Highcharts.Color(theme.neumorphic ? theme.primaryColor : '#00D0BE').setOpacity(0).get('rgba')], // TODO replace with  theme.primaryColor eventually
          ],
        },
      },
    },
  }), [data]);

  return <HighchartsReact containerProps={{style: {height, width}}}
                          highcharts={Highcharts}
                          options={options}/>;
};

export default withTheme(Line);

Line.propTypes = {
  height: PropTypes.string,
  width: PropTypes.string,
  title: PropTypes.string,
  // Y-Axis Scale
  // Number of Y-Axis Markers
  // X-Axis Scale
  // Number of X-Axis Markers
  data: PropTypes.array,
  theme: PropTypes.object,
};
